import { useForm } from 'react-hook-form';
import { FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import {
  Button,
  Container,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  TextField as MuiTextField,
  Box
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, getCurrentDateTime, getKeyPathValue, request, toast } from '../../../../../utils';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import { DischargeSummaryMedicineDetailsForm } from '../../HMS/transactions/DischargeSummaryMedicineDetailsForm';
import { ComplaintsForm } from './ComplaintsForm';
import { DischargeSummaryAddDiagnosForm } from '../../HMS/transactions/DischargeSummaryAddDiagnosForm';
import { OPDBillingDetailForm } from '../../HMS/transactions/OPDBillingDetailForm';

const PATIENT_TABLE_HEADER = [
  { header: 'UHID', accessor: 'uhid' },
  { header: 'OP No', accessor: 'op_no' },
  { header: 'Created At', accessor: 'createdAt' },
  { header: 'Patient Name', accessor: (row) => `${row.name_suffix} ${row.first_name} ${row.last_name}` },
  { header: 'Mobile No', accessor: 'address.mobile' },
  { header: 'Doctor', accessor: 'consultant.name' },
  { header: 'Address', accessor: 'address.address' }
];

const TABLE_HEADER = [
  {
    header: ' Date',
    accessor: (row) => `${getCurrentDateTime('date', row.createdAt)}`
  },
  {
    header: 'UHID No',
    accessor: 'uhid'
  },
  {
    header: 'Patient Name',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black'
          }}
        >
          {`${row?.patient?.name}`}
        </span>
      );
    }
  },
  {
    header: 'Sponsor',
    accessor: 'patient.sponsor'
  },
  {
    header: 'Mobile No',
    accessor: 'patient.mobile_no'
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  }
];

const PAST_VITAL_HISTORY_TABLE_HEADER = [
  {
    header: 'Vitals',
    accessor: (row) => {
      const vitals = [
        row.vital.bp_sys && `Bp Systolic ${row.vital.bp_sys}`,
        row.vital.diastol && `Diastol ${row.vital.diastol}`,
        row.vital.abdomen && `Abdomen ${row.vital.abdomen}`,
        row.vital.pulse && `Pulse ${row.vital.pulse}`,
        row.vital.dm && `DM ${row.vital.dm}`,
        row.vital.asthma && `Asthma ${row.vital.asthma}`,
        row.vital.ihd && `IHD ${row.vital.ihd}`,
        row.vital.con_level && `Con Level ${row.vital.con_level}`,
        row.vital.temp && `Temp ${row.vital.temp}`,
        row.vital.weight && `Weight ${row.vital.weight}`,
        row.vital.resp_rate && `Resp Rate ${row.vital.resp_rate}`,
        row.vital.chest && `Chest ${row.vital.chest}`,
        row.vital.cvs && `CVS ${row.vital.cvs}`,
        row.vital.dre && `Dre ${row.vital.dre}`,
        row.vital.cns && `Cns ${row.vital.cns}`,
        row.vital.any_other && `Any Other ${row.vital.any_other}`,
        row.vital.allergies && `Allergies ${row.vital.allergies}`,
        row.vital.height && `Height ${row.vital.height}`,
        row.vital.blood_group && `Blood Group ${row.vital.blood_group}`,
        row.vital.pallor && `Pallor ${row.vital.pallor}`,
        row.vital.icterus && `Icterus ${row.vital.icterus}`,
        row.vital.clubbing && `Clubbing ${row.vital.clubbing}`,
        row.vital.cyanos && `Cyanos ${row.vital.cyanos}`,
        row.vital.jvp && `JVP ${row.vital.jvp}`,
        row.vital.lymphade && `Lymphade ${row.vital.lymphade}`,
        row.vital.hydration && `Hydration ${row.vital.hydration}`
      ];
      return vitals.filter(Boolean).join(', ');
    }
  },
  {
    header: 'Date',
    accessor: 'createdAt'
  }
];

const COMPLAINT_DETAIL_HEADER = [
  {
    header: 'Complaint List',
    accessor: 'complaints'
  },
  {
    header: 'Duration',
    accessor: 'duration'
  },
  {
    header: 'Date',
    accessor: 'date'
  }
];

const PAST_HISTORY_COMPLAINTS = [
  {
    header: 'Complaint',
    accessor: (row) => row.complaints
  },
  {
    header: 'Date',
    accessor: 'createdAt'
  }
];

const DIAGNOSIS_LIST_HEADER = [
  {
    header: 'Diagnosis List',
    accessor: 'add_diagnos'
  },
  {
    header: 'Date',
    accessor: 'createdAt'
  }
];

const PAST_HISTORY_DIAGNOS = [
  {
    header: 'Diagnos',
    accessor: (row) => row.add_diagnos
  },
  {
    header: 'Date',
    accessor: 'createdAt'
  }
];

const SERVICE_LAB_HEADER = [
  {
    header: 'Type',
    accessor: 'type'
  },
  {
    header: 'Operating Doctor',
    accessor: 'doctor'
  },
  {
    header: 'Service Name',
    accessor: 'service_name'
  },
  {
    header: 'Qty',
    accessor: 'quantity'
  },
  {
    header: 'Rate',
    accessor: 'rate'
  },
  {
    header: 'Amount',
    accessor: 'amount'
  },
  {
    header: 'Dis. Amt.',
    accessor: ''
  },
  {
    header: 'Net. Amt.',
    accessor: 'net_amt'
  },
  {
    header: 'Date',
    accessor: 'date'
  }
];

const PAST_SERVICE_LAB_HISTORY_HEADER = [
  {
    header: 'Date',
    accessor: 'createdAt'
  },
  {
    header: 'Service',
    accessor: (row) => row.service.service_name
  },
  {
    header: 'Lab Test',
    accessor: (row) => row.service.test_name
  }
];

const MEDICINE_DETAIL_HEADER = [
  {
    header: 'Medicine Name',
    accessor: (row) => row?.medicine?.main?.name || row?.medicine
  },
  {
    header: 'Unit',
    accessor: 'unit' // Simple access as unit is directly in the row
  },
  {
    header: 'Frequency',
    accessor: (row) => row?.frequency?.frequency || row?.frequency // Access frequency from the frequency field
  },
  {
    header: 'Days',
    accessor: 'days' // Direct access to days
  },
  {
    header: 'Morning',
    accessor: 'morning' // Direct access to morning
  },
  {
    header: 'Noon',
    accessor: 'noon' // Direct access to noon
  },
  {
    header: 'Evening',
    accessor: 'evening' // Direct access to evening
  },
  {
    header: 'Night',
    accessor: 'night' // Direct access to night
  },
  {
    header: 'Date',
    accessor: 'createdAt' // Direct access to createdAt
  }
];

const PAST_MEDICINE_HISTORY_HEADER = [
  {
    header: 'Date',
    accessor: 'createdAt'
  },

  {
    header: 'Medicine',
    accessor: (row) => row.medicine_name
  }
];

const initialOPDBillingDataObj = {
  data: [],
  loading: false
};

export const OpdRequisition = () => {
  const doctorIDRef = useRef();
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const pathParts = pathname.split('/');
  const organisation = pathParts[1];
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [opdBillingDataObj, setOpdBillingDataObj] = useState(initialOPDBillingDataObj);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [searchPatientRegisterationDialog, setSearchPatientRegisterationDialog] = useState(false);
  const [patientDetailList, setPatientDetailList] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [complaints, setComplaints] = useState([]);
  const [departmentDropdown, setDepartmentDropdown] = useState([]);
  const [medicineDetailList, setmedicineDetailList] = useState([]);
  const [addDetailsDialogOpen, setAddDetailsDialogOpen] = useState(false);
  const [addComplaintDialogueOpen, setAddComplaintDialogueOpen] = useState(false);
  const [addDiagnosDialogOpen, setAddDiagnosDialogOpen] = useState(false);
  const [diagnos, setDiagnos] = useState([]);
  const [addMedicineDialogue, setAddMedicineDialogue] = useState(false);
  const [patientVitals, setPatientVitals] = useState([]);
  const [input, setInput] = useState('');
  const [originalData, setOriginalData] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [pastComplaints, setPastComplaints] = useState([]);
  const [pastDiagnos, setPastDiagnos] = useState([]);
  const [pastService, setPastService] = useState([]);
  const [pastMedicine, setPastMedicine] = useState([]);
  const [medicine, setMedicine] = useState([]);
  const [frequency, setFrequency] = useState([]);
  const [dose, setDose] = useState([]);
  const [serviceDropdown, setServiceDropdown] = useState([]);
  const [testDropdown, setTestDropdown] = useState([]);
  const user = JSON.parse(localStorage.getItem('user'));

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    const dayDifference = today.getDate() - birthDate.getDate();
    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
      age--;
    }
    return age;
  };

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        uhid: Yup.string().required(' ')
      })
    ),
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      time: getCurrentDateTime('time'),
      bill: {
        out_sourced: 'No',
        rec_amt_1: 0,
        rec_amt_2: 0,
        tds_amt: 0,
        disc_amt: 0,
        gst_amt: 0,
        net_amt: 0
      }
    },
    mode: 'all'
  });
  const { handleSubmit, watch, reset, setValue } = methods;

  const getDepartmentDropdown = useCallback(async () => {
    const data = await dropdownFetch('department');
    setDepartmentDropdown(data);
  }, []);

  useEffect(() => {
    getDepartmentDropdown();
  }, getDepartmentDropdown);

  useEffect(() => {
    let total_qty = 0;
    let total_amt = 0;
    let disc_amt = 0;
    let net_amt = 0;
    let gst_amt = 0;
    detailList &&
      detailList.forEach((detail) => {
        total_qty += detail.quantity;
        total_amt += detail.amount;
        disc_amt += detail.disc_amt;
        gst_amt += detail.gst_amt;
        net_amt += detail.net_amt;
      });
    setValue('bill.total_qty', total_qty);
    setValue('bill.total_amt', total_amt);
    setValue('bill.disc_amt', disc_amt);
    setValue('bill.gst_amt', gst_amt);
    setValue('bill.net_amt', net_amt);
  }, [detailList, setValue]);

  const netAmtWatch = watch('bill.net_amt');
  const recAmt1Watch = watch('bill.rec_amt_1');
  const recAmt2Watch = watch('bill.rec_amt_2');
  useEffect(() => {
    const total_recpt = +recAmt1Watch + +recAmt2Watch;
    setValue('bill.total_recpt', total_recpt);
    setValue('bill.balance_amt', netAmtWatch - total_recpt);
  }, [netAmtWatch, recAmt1Watch, recAmt2Watch, setValue]);

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, 'bill', [
        '__v',
        '_id',
        'createdAt',
        'status',
        'service_items',
        'uhid',
        'complaints',
        'diagnos',
        'medicine',
        'vital'
      ]);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
      setValue('uhid', selectedRecord.uhid);
      setDetailList(selectedRecord.service_items);
      setComplaints(selectedRecord.complaints);
      setDiagnos(selectedRecord.diagnos);
      setmedicineDetailList(selectedRecord.medicine);
      setValue('date', getCurrentDateTime('date', selectedRecord.createdAt));
      setValue('time', getCurrentDateTime('time', selectedRecord.createdAt));
      searchPatientHandler(selectedRecord.uhid);
      if (selectedRecord?.vital) {
        setValue('vital.bp_sys', selectedRecord?.vital.bp_sys);
        setValue('vital.diastol', selectedRecord?.vital.diastol);
        setValue('vital.abdomen', selectedRecord?.vital.abdomen);
        setValue('vital.pulse', selectedRecord?.vital.pulse);
        setValue('vital.dm', selectedRecord?.vital.dm);
        setValue('vital.ihd', selectedRecord?.vital.ihd);
        setValue('vital.con_level', selectedRecord?.vital.con_level);
        setValue('vital.temp', selectedRecord?.vital.temp);
        setValue('vital.weight', selectedRecord?.vital.weight);
        setValue('vital.resp_rate', selectedRecord?.vital.resp_rate);
        setValue('vital.chest', selectedRecord?.vital.chest);
        setValue('vital.cvs', selectedRecord?.vital.cvs);
        setValue('vital.dre', selectedRecord?.vital.dre);
        setValue('vital.cns', selectedRecord?.vital.cns);
        setValue('vital.any_other', selectedRecord?.vital.any_other);
        setValue('vital.allergies', selectedRecord?.vital.allergies);
        setValue('vital.height', selectedRecord?.vital.height);
        setValue('vital.blood_group', selectedRecord?.vital.blood_group);
        setValue('vital.pallor', selectedRecord?.vital.pallor);
        setValue('vital.cyanos', selectedRecord?.vital.cyanos);
        setValue('vital.jvp', selectedRecord?.vital.jvp);
        setValue('vital.lymphade', selectedRecord?.vital.lymphade);
        setValue('vital.hydration', selectedRecord?.vital.hydration);
        setValue('vital.asthma', selectedRecord?.vital?.asthma);
        setValue('vital.icterus', selectedRecord?.vital?.icterus);
        setValue('vital.clubbing', selectedRecord?.vital?.clubbing);
        setValue('follow_up_date', selectedRecord?.follow_up_date);
        setValue('follow_up_days', selectedRecord?.follow_up_days);
        setValue('advice', selectedRecord?.advice);
        setValue('remarks', selectedRecord?.remarks);
      }
    } else {
      reset();
    }
  }, [selectedRecord]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/opd-requisition-edit' : '/opd-requisition-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: {
        ...formValues,
        service_items: detailList,
        complaints: complaints,
        medicine: medicineDetailList,
        diagnos: diagnos,
        createdBy: user.name,
        updatedBy: editFlag ? user.name : '',
        organisation
      }
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'updated' : 'created'} OPD Requisition`, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
      reset();
    } else {
      toast(`Failed to ${editFlag ? 'update' : 'create'} OPD Requisition`, {
        variant: 'error'
      });
    }
    setLoading(false);
  };

  const handleRowDelete = (rowId) => {
    setDetailList((prev) =>
      prev.filter((data) => {
        return data.service !== rowId;
      })
    );
  };

  const handleComplaintDelete = (rowId) => {
    setComplaints((prev) =>
      prev.filter((data) => {
        return data.complaints != rowId;
      })
    );
  };

  const handleDiagnosDelete = (rowId) => {
    setDiagnos((prev) =>
      prev.filter((data) => {
        return data.add_diagnos != rowId;
      })
    );
  };

  const handleDeleteMedicine = (rowId) => {
    setmedicineDetailList((prev) =>
      prev.filter((data) => {
        return data.medicine_name != rowId;
      })
    );
  };

  const onDetailAddHandler = (data) => {
    console.log('HELLLO DETAIL HEADER S S S S ', data);
    const existingDetail = detailList.find(({ service }) => service === data.service);
    if (existingDetail) {
      toast('OPD Type Service already chosen', { variant: 'error' });
      return;
    }
    const serviceName = serviceDropdown.find(({ _id }) => _id === data.service)?.service_name;
    const testName = testDropdown.find(({ _id }) => _id === data.service)?.test_name;
    console.log('HELLO TEST NAME', testName);

    setDetailList((prev) => [
      ...prev,
      {
        ...data,
        service_name: serviceName || testName
      }
    ]);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/opd-requisition-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted OPD Requisition', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete OPD Requisition', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/opd-requisition-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully cancel OPD Requisition', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to cancel OPD Requisition', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setOpdBillingDataObj({ loading: true });
    const response = await request('/opd-requisition-list', { method: 'GET' });
    if (response.success) {
      const sortedData = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      const filteredData = sortedData.filter(
        (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
      );
      setOpdBillingDataObj({ loading: false, data: filteredData });
      setOriginalData(filteredData);
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setOpdBillingDataObj({ loading: false });
    }
  };

  const searchPatientHandler = async (uhid = null) => {
    if (typeof uhid !== 'number' && typeof uhid !== 'string') uhid = null;
    setLoading(true);
    const today = new Date().toISOString().split('T')[0];
    if (!uhid) setSearchPatientRegisterationDialog(true);
    const response = await request('/patient-registeration-searchdetail', {
      method: 'GET',
      query: { uhid }
    });
    if (response?.success) {
      if (uhid) {
        const {
          data: [patientData]
        } = response;
        setValue('visit', patientData.visit_no);
        setValue('op', patientData.op_no);
        setValue('patient_name', `${patientData.first_name} ${patientData.last_name}`);
        setValue('patient_suffix', patientData.name_suffix);
        setValue('opd_type', patientData.opd_type?.type);
        setValue('ref_type', patientData.ref_by?.referral_name);
        setValue('application_no', patientData.app_no_date);
        setValue('doctor', patientData.consultant?.name);
        doctorIDRef.current = patientData.consultant?._id;
        setValue('department', patientData.department?.department);
        setValue('patient_age', calculateAge(patientData?.dob));
        setValue('sponsor', patientData.sponsor?.name);
        setValue('remarks', patientData.remarks);
        setValue('queue_no', patientData.app_no_date);
        setValue('card_no', patientData.chq_card_no);
        setValue('vertical', patientData.vertical?.verticalname);
      } else {
        const todayData = response.data.filter(
          (patient) => new Date(patient.createdAt).toISOString().split('T')[0] === today
        );
        setPatientDetailList(todayData);
      }
    } else {
      toast('Failed to load patient details', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleActiveTab = (newTabIndex) => {
    setActiveTab(newTabIndex);
  };

  useEffect(() => {
    if (opdBillingDataObj.data && opdBillingDataObj.data.length > 0) {
      setOriginalData(opdBillingDataObj.data);
      setIsFiltered(false);
    }
  }, [opdBillingDataObj.data]);

  useEffect(() => {
    handleUhidSearch();
  }, [input]);

  const handleUhidSearch = () => {
    const lowercasedInput = input.toLowerCase();
    console.log('HELLO BILLING DATA OBJ', opdBillingDataObj);
    const filtered = opdBillingDataObj.data.filter(
      (item) =>
        item?.uhid?.toString().toLowerCase().includes(lowercasedInput) ||
        item?.patient?.name?.toLowerCase().includes(lowercasedInput) ||
        item?.patient?.mobile_no?.toLowerCase().includes(lowercasedInput)
    );
    setOriginalData(filtered);
  };

  const handleChange = (e) => {
    const newInput = e.target.value;
    setInput(newInput);
  };

  const getMedicineDropdown = useCallback(async () => {
    const data = await dropdownFetch('product-master');
    setMedicine(data);
  }, []);

  const getFrequencyDropdown = useCallback(async () => {
    const data = await dropdownFetch('frequency-master');
    setFrequency(data);
  }, []);

  const getDoseDropdown = useCallback(async () => {
    const data = await dropdownFetch('dose-master');
    setDose(data);
  }, []);

  const getServiceDropdown = useCallback(async () => {
    const data = await dropdownFetch('service');
    setServiceDropdown(data);
  }, []);

  const getTestDropdown = useCallback(async () => {
    const data = await dropdownFetch('lis-test');
    setTestDropdown(data);
  }, []);

  useEffect(() => {
    getMedicineDropdown();
    getFrequencyDropdown();
    getDoseDropdown();
    getServiceDropdown();
    getTestDropdown();
  }, [getMedicineDropdown, getFrequencyDropdown, getDoseDropdown, getServiceDropdown]);

  const onAddDetailHandler = (row) => {
    console.log(row, 'HELLLO THE ROW DETAIL IS HERE');

    // Check if the medicine is already in the details
    const existingDetail = medicineDetailList.find(({ medicine_name }) => medicine_name === row.medicine_name);
    if (existingDetail) {
      toast('Medicine already chosen', { variant: 'error' });
      return;
    }

    // Find the medicine data based on the given medicine's product ID (row.medicine_name)
    const medicineData = medicine.find(({ _id }) => _id === row.medicine_name);
    const frequencyData = frequency.find(({ _id }) => _id === row.frequency);

    console.log('HELLLLO MEDICINE DATATATATTATA', medicineData);

    // Extract the product name and other relevant details from the medicineData
    const productName = medicineData?.main?.name;
    const medicineId = medicineData?._id;

    // Extract the frequency data
    const frequencyName = frequencyData?.frequency; // frequency name
    const frequencyId = frequencyData?._id; // frequency ID

    // If product name is not found, show an error
    if (!productName) {
      toast('Product name not found', { variant: 'error' });
      return;
    }

    // Add the new medicine detail to the state
    setmedicineDetailList((prev) => [
      ...prev,
      {
        medicine: productName, // The name of the medicine
        medicine_name: medicineId, // The ID of the medicine
        frequency: frequencyName, // Add frequency name here
        frequency_name: frequencyId, // Add frequency name here
        unit: row.unit, // Add unit from row
        days: row?.days, // Add days from row
        frequencyId: frequencyId, // Add frequency ID here
        morning: row.morning, // Add morning dosage info
        noon: row.noon, // Add noon dosage info
        evening: row.evening, // Add evening dosage info
        night: row.night // Add night dosage info
      }
    ]);
  };

  useEffect(() => {
    console.log(pastMedicine, 'HELLLO THE PAST MEDICINE');
  }, [pastMedicine]);

  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 700 }}>
                OPD Requisition
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    setDetailList([]);
                    reset();
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  onClick={() => window.open(`/print${pathname}?refId=${selectedRecord._id}`, '_blank')}
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction)}
                  onClick={() => window.open(`/print${pathname}/ProgressReport?refId=${selectedRecord._id}`, '_blank')}
                >
                  Progress Report
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '100%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={4}
                  sx={{ display: 'flex', gap: 0.3, flexDirection: 'column', boxShadow: '0px 2px 2px #0099ff' }}
                >
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 600, fontSize: '1.4rem' }}>Depart.</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.7 }}
                      name="department"
                      menus={departmentDropdown.map((data) => ({ ...data, name: data.department }))}
                    />
                    <Button
                      sx={{ flex: 0.1, minWidth: 0, p: 0.25, height: '100%' }}
                      variant="contained"
                      onClick={searchPatientHandler}
                      disabled={!['new', 'edit'].includes(currentAction)}
                    >
                      <SearchIcon />
                    </Button>
                  </Stack>
                  <Stack direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.21, fontWeight: 600, fontSize: '1.4rem' }}>Date</Typography>
                    <TextField sx={{ flex: 0.8 }} name="date" type="date" />
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{ display: 'flex', gap: 0.3, flexDirection: 'column', boxShadow: '0px 2px 2px #0099ff' }}
                >
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 600, fontSize: '1.4rem' }}>Doc No./Date</Typography>
                    <TextField disabled={true} sx={{ flex: 0.4 }} placeholder="Doc No." name="doc_no" />
                    <TextField disabled={true} sx={{ flex: 0.4 }} placeholder="Bill No." name="date" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 600, fontSize: '1.4rem' }}>Patient/Age</Typography>
                    <TextField disabled={true} name="patient_name" sx={{ flex: 0.4 }} placeholder="Patient Name" />
                    <TextField disabled={true} name="patient_age" sx={{ flex: 0.4 }} placeholder="Age" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 600, fontSize: '1.4rem' }}>Doctor</Typography>
                    <TextField disabled={true} sx={{ flex: 0.8 }} name="doctor" placeholder="Doctor" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 600, fontSize: '1.4rem' }}>Ac. Jr.id</Typography>
                    <SelectTag disabled={true} formControlSX={{ flex: 0.8 }} name="ac_jr_id" />
                  </Stack>
                </Grid>
                <Grid
                  item
                  xs={4}
                  sx={{ display: 'flex', gap: 0.3, flexDirection: 'column', boxShadow: '0px 2px 2px #0099ff' }}
                >
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 600, fontSize: '1.4rem' }}>UHID No</Typography>
                    <TextField sx={{ flex: 0.8 }} name="uhid" placeholder="Search By Uhid " disabled />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 600, fontSize: '1.4rem' }}>Op/Mobile No.</Typography>
                    <TextField disabled={true} sx={{ flex: 0.4 }} name="op" placeholder="Op No." />
                    <TextField disabled={true} sx={{ flex: 0.4 }} name="mobile" placeholder="Mobile No." />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 600, fontSize: '1.4rem' }}>Department</Typography>
                    <TextField disabled sx={{ flex: 0.8 }} name="department" placeholder="Department" />
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Button
                      variant="contained"
                      onClick={() => handleActiveTab(0)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 900,
                        backgroundColor: activeTab === 0 ? 'primary.main' : 'default',
                        color: activeTab === 0 ? 'white' : 'text.primary'
                      }}
                    >
                      Vital
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleActiveTab(1)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 900,
                        backgroundColor: activeTab === 1 ? 'primary.main' : 'default',
                        color: activeTab === 1 ? 'white' : 'text.primary'
                      }}
                    >
                      Complaints
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleActiveTab(2)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 700,
                        fontWeight: 900,
                        backgroundColor: activeTab === 2 ? 'primary.main' : 'default',
                        color: activeTab === 2 ? 'white' : 'text.primary'
                      }}
                    >
                      Diagnosis
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleActiveTab(3)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 900,
                        backgroundColor: activeTab === 3 ? 'primary.main' : 'default',
                        color: activeTab === 3 ? 'white' : 'text.primary'
                      }}
                    >
                      Service Lab
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleActiveTab(4)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 900,
                        backgroundColor: activeTab === 4 ? 'primary.main' : 'default',
                        color: activeTab === 4 ? 'white' : 'text.primary'
                      }}
                    >
                      Medicine
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleActiveTab(5)}
                      sx={{
                        my: 0,
                        mx: 1,
                        borderRadius: '4px',
                        fontWeight: 900,
                        backgroundColor: activeTab === 5 ? 'primary.main' : 'default',
                        color: activeTab === 5 ? 'white' : 'text.primary'
                      }}
                    >
                      Advice & Other
                    </Button>
                  </Box>

                  {activeTab === 0 && (
                    <>
                      {/* <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}> */}
                      <Stack sx={{ mb: 0.3, marginTop: '2%' }} direction="row" spacing={2}>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Bp Sys.</Typography>
                          <TextField
                            sx={{ flex: 0.8 }}
                            name="vital.bp_sys"
                            placeholder="BP Systolic"
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Diastol</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.diastol"
                            placeholder="BP Diastolic"
                          />
                        </Stack>

                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Abdomen</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.abdomen"
                            placeholder="Abdomen"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Pulse</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.pulse"
                            placeholder="Pulse"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>DM</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.dm"
                            placeholder="DM"
                          />
                        </Stack>
                      </Stack>

                      <Stack sx={{ mb: 0.3 }} direction="row" spacing={2}>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Asthma</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.asthma"
                            placeholder="Asthma"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>IHD</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.ihd"
                            placeholder="IHD"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Con. Level</Typography>
                          <TextField
                            sx={{ flex: 0.8 }}
                            name="vital.con_level"
                            placeholder="Cons Level"
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Temp</Typography>
                          <TextField
                            sx={{ flex: 0.8 }}
                            name="vital.temp"
                            placeholder="Temprature"
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Weight</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.weight"
                            placeholder="Weight"
                          />
                        </Stack>
                      </Stack>

                      <Stack sx={{ mb: 0.3 }} direction="row" spacing={2}>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Resp. Rate</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.resp_rate"
                            placeholder="Resp. Rate"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Chest</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.chest"
                            placeholder="Chest"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Cvs</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.cvs"
                            placeholder="Cvs"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Dre</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.dre"
                            placeholder="Dre"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Cns</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.cns"
                            placeholder="Cns"
                          />
                        </Stack>
                      </Stack>

                      <Stack sx={{ mb: 0.3 }} direction="row" spacing={2}>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Any Other</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.any_other"
                            placeholder="Any Other"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Allergies</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.allergies"
                            placeholder="Allergies"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Height</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.height"
                            placeholder="Height"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.5, fontWeight: 600, fontSize: '1.4rem' }}>Blood Group</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.blood_group"
                            placeholder="Blood Group"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Pallor</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.pallor"
                            placeholder="Pallor"
                          />
                        </Stack>
                      </Stack>
                      <Stack sx={{ mb: 0.3 }} direction="row" spacing={2}>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Icterus</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.icterus"
                            placeholder="Icterus"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Clubbing</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.clubbing"
                            placeholder="Clubbing"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Cyanos</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="vital.cyanos"
                            placeholder="Cyanos"
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Jvp</Typography>
                          <TextField
                            sx={{ flex: 0.8 }}
                            name="vital.jvp"
                            placeholder="Jvp"
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.4, fontWeight: 600, fontSize: '1.4rem' }}>Lymphade</Typography>
                          <TextField
                            sx={{ flex: 0.8 }}
                            name="vital.lymphade"
                            placeholder="Lymphade"
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Stack>
                      </Stack>
                      <Stack sx={{ mb: 0.3 }} direction="row" spacing={0.5}>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 5 }}>
                          <Typography sx={{ flex: 0.32, fontWeight: 600, fontSize: '1.4rem' }}>Hydration</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.62 }}
                            name="vital.hydration"
                            placeholder="Hydration"
                          />
                        </Stack>
                      </Stack>
                      <Grid item xs={12}>
                        <Typography fontWeight="bold" sx={{ fontSize: '14px', color: 'red' }}>
                          Past Vital History
                        </Typography>
                        <Divider />
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Table
                          containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                          data={patientVitals?.vital || []}
                          columns={PAST_VITAL_HISTORY_TABLE_HEADER}
                          size="small"
                          onDeleteHandler={handleRowDelete}
                        />
                      </Grid>
                    </>
                  )}
                  {activeTab === 1 && (
                    <>
                      <Button
                        sx={{ marginTop: '1%' }}
                        variant="contained"
                        onClick={() => setAddComplaintDialogueOpen(true)}
                        disabled={!['new', 'edit'].includes(currentAction)}
                      >
                        Add New
                      </Button>
                      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Table
                          containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                          columns={COMPLAINT_DETAIL_HEADER}
                          data={complaints.map((data) => ({ _id: data.complaints, ...data })) || []}
                          showDeleteAction={true}
                          onDeleteHandler={handleComplaintDelete}
                          disabled={!['new', 'edit'].includes(currentAction)}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography fontWeight="bold" sx={{ fontSize: '14px', color: 'red' }}>
                          Past Complaint History
                        </Typography>
                        <Divider />
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Table
                          containerSX={{ flex: 1, border: '0px solid rgba(0,0,0,0.27)' }}
                          data={pastComplaints?.complaints || []}
                          columns={PAST_HISTORY_COMPLAINTS}
                          size="small"
                          onDeleteHandler={handleRowDelete}
                        />
                      </Grid>
                    </>
                  )}
                  {activeTab === 2 && (
                    <>
                      <Button
                        sx={{ marginTop: '1%' }}
                        variant="contained"
                        onClick={() => setAddDiagnosDialogOpen(true)}
                        disabled={!['new', 'edit'].includes(currentAction)}
                      >
                        Add New
                      </Button>

                      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Table
                          containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                          columns={DIAGNOSIS_LIST_HEADER}
                          data={diagnos.map((data) => ({ _id: data.add_diagnos, ...data })) || []}
                          showDeleteAction={true}
                          onDeleteHandler={handleDiagnosDelete}
                          disabled={!['new', 'edit'].includes(currentAction)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography fontWeight="bold" sx={{ fontSize: '14px', color: 'red' }}>
                          Past Diagnos History
                        </Typography>
                        <Divider />
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Table
                          containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                          data={pastDiagnos?.diagnos || []}
                          columns={PAST_HISTORY_DIAGNOS}
                          size="small"
                          onDeleteHandler={handleRowDelete}
                        />
                      </Grid>
                    </>
                  )}
                  {activeTab === 3 && (
                    <>
                      <Button
                        variant="contained"
                        sx={{ marginTop: '1%' }}
                        onClick={() => setAddDetailsDialogOpen(true)}
                        disabled={!['new', 'edit'].includes(currentAction)}
                      >
                        Add Details
                      </Button>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Table
                            containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                            columns={SERVICE_LAB_HEADER}
                            data={detailList.map((data) => ({ _id: data.service, ...data })) || []}
                            showDeleteAction={true}
                            onDeleteHandler={handleRowDelete}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography sx={{ fontWeight: 'bold', color: 'red', fontSize: '14px' }}>
                            Past Service & Lab History
                          </Typography>
                          <Table
                            containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                            columns={PAST_SERVICE_LAB_HISTORY_HEADER}
                            data={pastService.service_items || []}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}
                  {activeTab === 4 && (
                    <>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', marginTop: '1%' }}>
                          <Button
                            variant="contained"
                            sx={{ width: '10%' }}
                            onClick={() => setAddMedicineDialogue(true)}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          >
                            Add Medicine
                          </Button>
                          <Table
                            containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                            columns={MEDICINE_DETAIL_HEADER}
                            data={medicineDetailList.map((data) => ({ _id: data.medicine_name, ...data })) || []}
                            showDeleteAction={true}
                            onDeleteHandler={handleDeleteMedicine}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography sx={{ fontWeight: 'bold', color: 'red', fontSize: '14px', fontWeight: 600 }}>
                            Past Medicine History
                          </Typography>
                          <Table
                            containerSX={{ flex: 1, border: '1px solid rgba(0,0,0,0.27)' }}
                            columns={PAST_MEDICINE_HISTORY_HEADER}
                            data={pastMedicine.medicine || []}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}
                  {activeTab === 5 && (
                    <>
                      {/* <Grid item xs={4} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}> */}
                      <Stack sx={{ mb: 0.3, marginTop: '2%' }} direction="row" spacing={2}>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 2 }}>
                          <Typography sx={{ flex: 0.56, fontWeight: 600, fontSize: '14px' }}>Follow Up Date</Typography>
                          <TextField
                            sx={{ flex: 0.8 }}
                            name="follow_up_date"
                            type="date"
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Stack>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 / 2 }}>
                          <Typography sx={{ flex: 0.2, fontWeight: 600, fontSize: '14px' }}>Follow Up Days</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="follow_up_days"
                            placeholder="Follow Up Days"
                          />
                        </Stack>
                      </Stack>
                      <Stack sx={{ mb: 0.3 }} direction="row" spacing={2}>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 }}>
                          <Typography sx={{ flex: 0.2, fontWeight: 600, fontSize: '14px' }}>Advice</Typography>
                          <TextField
                            disabled={!['new', 'edit'].includes(currentAction)}
                            sx={{ flex: 0.8 }}
                            name="advice"
                            multiline
                            rows={5}
                            placeholder="Advice"
                          />
                        </Stack>
                      </Stack>
                      <Stack sx={{ mb: 0.3 }} direction="row" spacing={2}>
                        <Stack spacing={0.5} direction="row" alignItems="center" sx={{ flex: 1 }}>
                          <Typography sx={{ flex: 0.2, fontWeight: 600, fontSize: '14px' }}>Remarks</Typography>
                          <TextField
                            sx={{ flex: 0.8 }}
                            name="remarks"
                            multiline
                            rows={5}
                            placeholder="Remarks"
                            disabled={!['new', 'edit'].includes(currentAction)}
                          />
                        </Stack>
                      </Stack>
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            OPD Requisition Query Window
          </Typography>

          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '14px'
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 14px'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem' // Adjust label font size
              }
            }}
            label="Search Patient"
            variant="outlined"
            value={input}
            onChange={handleChange}
          />
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_HEADER}
          data={originalData || []}
          loading={opdBillingDataObj.loading}
          onRowClick={(row) => {
            delete row.patient;
            setSelectedRecord(row);
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
      <Dialog
        PaperProps={{ sx: { width: '80%', p: 1.5 } }}
        open={searchPatientRegisterationDialog}
        onClose={() => setSearchPatientRegisterationDialog(false)}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0.2}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Patient Query Window
          </Typography>
          <IconButton onClick={() => setSearchPatientRegisterationDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={PATIENT_TABLE_HEADER}
          data={patientDetailList || []}
          onRowClick={(row) => {
            setValue('uhid', row.uhid);
            setValue('visit', row.visit_no);
            setValue('op', row.op_no);
            setValue('patient_name', `${row.first_name} ${row.last_name}`);
            setValue('patient_suffix', row.name_suffix);
            setValue('opd_type', row.opd_type?.type);
            setValue('ref_type', row.ref_by?.referral_name);
            setValue('application_no', row.app_no_date);
            setValue('doctor', row.consultant?.name);
            doctorIDRef.current = row.consultant?._id;
            setValue('department', row.department?.department);
            setValue('sponsor', row.sponsor?.name);
            setValue('remarks', row.remarks);
            setValue('queue_no', row.app_no_date);
            setValue('card_no', row.chq_card_no);
            setValue('vertical', row.vertical?.verticalname);
            setValue('patient_age', calculateAge(row.dob));
            setValue('mobile', row?.address?.mobile);
            setSearchPatientRegisterationDialog(false);
            setPatientVitals(row || {});
            setPastComplaints(row || {});
            setPastDiagnos(row || {});
            setPastService(row || {});
            setPastMedicine(row || {});
            console.log('HELLO PATIENT VITALS', patientVitals);
          }}
        />
      </Dialog>
      <Dialog
        open={addMedicineDialogue}
        onClose={() => setAddMedicineDialogue(false)}
        PaperProps={{ sx: { p: 2, width: '35rem' } }}
        maxWidth="xl"
        scroll="body"
      >
        <DischargeSummaryMedicineDetailsForm
          onClose={() => setAddMedicineDialogue(false)}
          medicine={medicine}
          frequency={frequency}
          dose={dose}
          onAdd={(row) => {
            onAddDetailHandler(row);
          }}
        />
      </Dialog>
      <Dialog
        open={addComplaintDialogueOpen}
        onClose={() => setAddComplaintDialogueOpen(false)}
        PaperProps={{ sx: { p: 2, width: '35rem' } }}
        maxWidth="xl"
        scroll="body"
      >
        <ComplaintsForm
          onClose={() => setAddComplaintDialogueOpen(false)}
          onAdd={(row) => {
            if (complaints.some((complaint) => complaint.complaints === row.complaints)) {
              toast('Complaint already taken', { variant: 'error' });
            } else {
              setComplaints((prev) => [...prev, row]);
            }
          }}
        />
      </Dialog>
      <Dialog
        open={addDiagnosDialogOpen}
        onClose={() => setAddDiagnosDialogOpen(false)}
        PaperProps={{ sx: { p: 2, width: '35rem' } }}
        maxWidth="xl"
        scroll="body"
      >
        <DischargeSummaryAddDiagnosForm
          onClose={() => setAddDiagnosDialogOpen(false)}
          onAdd={(row) => {
            if (diagnos.some((diagnos) => diagnos.add_diagnos === row.add_diagnos)) {
              toast('Diagnos already taken', { variant: 'error' });
            } else {
              setDiagnos((prev) => [...prev, row]);
            }
          }}
        />
      </Dialog>
      <Dialog
        open={addDetailsDialogOpen}
        onClose={() => setAddDetailsDialogOpen(false)}
        PaperProps={{ sx: { width: '80%', height: '90vh', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <OPDBillingDetailForm
          doctorID={doctorIDRef.current}
          onClose={() => setAddDetailsDialogOpen(false)}
          onAdd={onDetailAddHandler}
          serviceDropdown={serviceDropdown}
          testDropdown={testDropdown}
        />
      </Dialog>
    </>
  );
};
